import { useState, useEffect, ChangeEvent, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Select, Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import templatesApi from "api/landlord/templates/api";
import { Template } from "types/landlord/verificationTemplate";
import { components } from "react-select";
import { HugeiconsIcon } from "@hugeicons/react";
import { File02Icon } from "@hugeicons-pro/core-stroke-rounded";

const TemplateLabel = (props: { name: string; isDefault: boolean }) => {
  const { name, isDefault } = props;
  return (
    <div>
      <span className="text-base font-medium text-content-1000">{name} </span>
      <Show if={isDefault}>
        <span className="text-base text-content-200">(Property default)</span>
      </Show>
    </div>
  );
};

// Need a function to capture the templates and defaultTemplateData for use in the custom component.
// There may be a better way to do this using react-select's definition of SingleValueProps, but
// the documentation is not super clear on the type definition for SingleValueProps.
const getCustomSingleValue = (
  templates?: Template[],
  defaultTemplateData?: Template
) => {
  const CustomSingleValue = (props: any) => {
    const { data } = props;
    const template = templates?.find((t) => t.id === data.value);

    return (
      <components.SingleValue {...props}>
        <div className="flex items-center gap-2">
          <HugeiconsIcon icon={File02Icon} size={20} />
          <span>{template?.name}</span>
          {template?.id === defaultTemplateData?.id && (
            <span className="text-content-200">(Property default)</span>
          )}
        </div>
      </components.SingleValue>
    );
  };

  return CustomSingleValue;
};

interface TemplateSelectorProps {
  propertyId?: number;
  defaultTemplateId?: string;
  onTemplateChange: (template: Template | undefined) => void;
}

export const TemplateSelector = ({
  propertyId,
  defaultTemplateId,
  onTemplateChange
}: TemplateSelectorProps) => {
  const { permissions } = useAuthContext();
  const canOverride = Boolean(
    permissions.canOverrideApplicantScreeningTemplate
  );

  const [currentTemplateId, setCurrentTemplateId] = useState<
    string | undefined
  >();

  const { data: templates } = useQuery({
    queryKey: ["TEMPLATES"],
    queryFn: () => templatesApi.getTemplates(1, 999).then((res) => res.items)
  });

  const defaultTemplateData = useMemo(() => {
    if (!templates || !defaultTemplateId) return undefined;

    return templates.find((t) => t.id === defaultTemplateId);
  }, [templates, defaultTemplateId]);

  useEffect(() => {
    if (defaultTemplateId && defaultTemplateData) {
      setCurrentTemplateId(defaultTemplateId);
      onTemplateChange(defaultTemplateData);
    }
  }, [defaultTemplateId, defaultTemplateData, onTemplateChange]);

  const handleTemplateChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const selectedTemplate = templates?.find((t) => t.id === value);
    setCurrentTemplateId(value);
    onTemplateChange(selectedTemplate);
  };

  const templateOptions = useMemo(
    () =>
      templates?.map((template) => ({
        value: template.id,
        label: template.name
      })) || [],
    [templates]
  );

  if (!propertyId) return null;

  return (
    <div className="space-y-2">
      <Select
        label="Screening template"
        hideOptional
        // Force re-render when template loads. Otherwise default template is not selected by default
        key={currentTemplateId || "loading"}
        id="template-selector"
        value={currentTemplateId}
        onChange={handleTemplateChange}
        options={templateOptions}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        searchable={true}
        formatOptionLabel={(option, { context }) => {
          // For dropdown options, show the full formatted label
          if (context === "menu" && "value" in option) {
            const template = templates?.find((t) => t.id === option.value);
            return template ? (
              <TemplateLabel
                name={template.name}
                isDefault={template.id === defaultTemplateData?.id}
              />
            ) : (
              <span>{option.label}</span>
            );
          }
          // For the input value, just return the name
          return <span>{option.label}</span>;
        }}
        components={{
          SingleValue: getCustomSingleValue(templates, defaultTemplateData)
        }}
        isDisabled={!canOverride}
      />
    </div>
  );
};
